import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { capitalize } from 'lodash';
import {
  Button,
  Col,
  ControlLabel,
  FormGroup,
  HelpBlock,
  Row,
} from 'react-bootstrap';
import { Field } from 'react-final-form';
import { getOrgTermsContent } from '../../content/utils.js';
import { RATINGS } from '../../lib/schema.js';
import CheckboxAdapter from '../CheckboxAdapter.jsx';
import DatePickerAdapter from '../DatePickerAdapter.jsx';
import { validateEditorData } from '../editor/defaultOptions.js';
import FieldGroup from '../FieldGroup.jsx';
import RadioOptionsField from '../RadioOptionsField.jsx';
import InitiativeExampleCSVDownloadButton from './InitiativeExampleCSVDownloadButton.jsx';
import ParticipantsCurrentCSVDownloadButton from '../ParticipantsCurrentCSVDownloadButton.jsx';
import AuthorizedAction from '../AuthorizedAction.js';
import CategorySelect from './InitiativeFormFields/CategorySelect.jsx';
import { selectInitiativeCategoriesWithArchived } from '../../modules/initiativeCategories.js';
import StakeholdersSelect from '../stakeholders-select/StakeholdersSelect.jsx';
import ParticipantsSelect from './InitiativeFormFields/ParticipantsSelect.jsx';
import StakeholdersExampleCSVButton from '../StakeholdersExampleCSVButton.jsx';
import StakeholdersCurrentCSVDownloadButton from '../StakeholdersCurrentCSVDownloadButton.jsx';
import { getOrgSlug } from '../../lib/utils.js';
import styles from './InitiativeForm.module.css';
import Editor from '../editor/editor.jsx';

const impactOptions = RATINGS.HIGH_MEDIUM_LOW.map((rating) => ({
  label: capitalize(rating),
  value: rating,
}));

const mapStateToProps = (state) => ({
  initiativeCategories: selectInitiativeCategoriesWithArchived(state),
});

const InitiativeForm = (props) => {
  const {
    formRef,
    editing,
    onShowPreview,
    onCancel,
    isLoading,
    initiativeType,
    isCommunication,
    entities,
    orgStakeholders,
    organization,
    initiativeCategories,
    onDownloadStakeholdersCsv,
    onDownloadGroupCsv,
    onSelectGroup,
    onSelectAllGroupEmployees,
    onSelectUngroupedEmployee,
    onSelectIndividualStakeholder,
    onSelectGroupStakeholder,
    onShowGroupModal,
    onShowInitiativeCategoryModal,
    onShowManageGroupModal,
    onShowManageParticipantsModal,
    participantsEditAllowed,
    setParticipantsReplaceMethod,
    setStakeholdersReplaceMethod,
    setUploadStakeholdersMethod,
    setUploadParticipantsMethod,
    setContentEditorInstance,
    ungroupedSearchParams,
    handleSubmit,
    submitFailed,
    submitError,
    values,
  } = props;

  const {
    groups,
    ungroupedEmployees,
    uploadParticipantsMethod,
    replaceParticipantsMethod,
    groupStakeholders,
    individuals,
    uploadStakeholdersMethod,
    replaceStakeholdersMethod,
  } = entities;

  useEffect(() => {
    values.csv_file = '';
  }, [uploadParticipantsMethod]);

  useEffect(() => {
    values.csv_stakeholders_file = '';
  }, [uploadStakeholdersMethod]);

  const renderPreviewBtn = () => {
    if (!participantsEditAllowed) return null;
    if (values.csv_file && values?.csv_stakeholders_file) return null;
    if (!Object.keys(groups).length && !groupStakeholders.length) return null;
    return (
      <Button
        type="button"
        onClick={() => onShowPreview(values, { isPreview: true })}
        bsStyle="success"
      >
        {isLoading ? 'Loading...' : 'Preview in New Tab'}
      </Button>
    );
  };

  const getContentName = (id) => {
    const category = initiativeCategories
      .filter((category) => category.initiativeType === 'communication')
      .find((category) => category.id === Number(id));

    return category ? category.name : '';
  };

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      <Row>
        <Col xs={8}>
          {isCommunication && (
            <p className={`${styles.communicationHelp} text-muted`}>
              Add communication information below and click save. Completing the
              following communication fields assists for
              <span className="text-success"> tracking purposes only</span>. No
              communication will be sent during this step.
            </p>
          )}
        </Col>
      </Row>

      <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
        <h3>General</h3>
      </div>

      <Row>
        <Col xs={8}>
          <Field
            id="initiativeFormTitle"
            name="title"
            label="Title"
            placeholder={getOrgTermsContent('initiativeLabels.title')}
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={8}>
          <Field
            id="initiativeFormLead"
            name="lead"
            label="Lead"
            placeholder={getOrgTermsContent('initiativeLabels.leader')}
            type="text"
            component={FieldGroup}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={isCommunication ? 8 : 4}>
          <Field
            id="initiativeFormStartDate"
            name="start_date"
            label={isCommunication ? 'Date' : 'Start Date'}
            placeholderText="Select a Date"
            component={DatePickerAdapter}
            allowNull
          />
        </Col>
        {!isCommunication && (
          <Col sm={4}>
            <Field
              id="initiativeFormEndDate"
              name="end_date"
              label="End Date"
              placeholderText="Select a Date"
              component={DatePickerAdapter}
              allowNull
            />
          </Col>
        )}
      </Row>

      <Row className="m-b-24">
        <Col xs={8}>
          <CategorySelect
            initiativeType={initiativeType}
            initiativeSelectedCategory={values.category}
          />
        </Col>
        <Col xs={8}>
          <div className="flex flex-justify">
            {organization?.canManageCustomSettings ? (
              <React.Fragment>
                <AuthorizedAction action="manage" subject="dynamicCategory">
                  <a
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      onShowInitiativeCategoryModal();
                    }}
                  >
                    {' '}
                    + Add New Category
                  </a>
                </AuthorizedAction>

                <AuthorizedAction action="manage" subject="dynamicCategory">
                  <a
                    href={`/${getOrgSlug()}/organization/custom-settings`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Manage Categories
                  </a>
                </AuthorizedAction>
              </React.Fragment>
            ) : null}
          </div>
        </Col>
      </Row>
      {isCommunication &&
        !!values &&
        values.category !== undefined &&
        !!initiativeCategories && (
          <Row>
            <Col xs={8}>
              <Field name="content">
                {({ name, input }) => {
                  return (
                    <Editor
                      name={name}
                      data={validateEditorData(input.value)}
                      label={`${getContentName(values.category)} Content`}
                      setEditorInstanceForParent={setContentEditorInstance}
                      options={{
                        holder: 'content-initiative-editor',
                      }}
                    />
                  );
                }}
              </Field>
            </Col>
          </Row>
        )}

      <Row>
        <Col sm={5}>
          <RadioOptionsField
            id="initiativeFormImpact"
            label="Impact"
            name="impact"
            options={impactOptions}
          />
        </Col>
        {!isCommunication && (
          <Col sm={5}>
            <FormGroup>
              <ControlLabel>Tracking</ControlLabel>
              <Field
                id="initiativeFormCompletionTracked"
                name="completion_tracked"
                label="Track Completion"
                type="checkbox"
                component={CheckboxAdapter}
              />
              <Field
                id="initiativeFormMandatory"
                name="mandatory"
                label="Mandatory"
                type="checkbox"
                component={CheckboxAdapter}
              />
            </FormGroup>
          </Col>
        )}
      </Row>

      {participantsEditAllowed ? (
        <React.Fragment>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Add Participants</h3>
          </div>

          <Row className="m-b-24">
            <Col xs={8}>
              <ParticipantsSelect
                selectedGroups={groups}
                selectedUngrouped={ungroupedEmployees}
                onSelectGroup={onSelectGroup}
                onSelectAllGroupEmployees={onSelectAllGroupEmployees}
                onSelectUngroupedEmployee={onSelectUngroupedEmployee}
                ungroupedSearchParams={ungroupedSearchParams}
                uploadMethod={uploadParticipantsMethod}
                setUploadMethod={setUploadParticipantsMethod}
                replaceMethod={replaceParticipantsMethod}
                setReplaceMethod={setParticipantsReplaceMethod}
                onShowGroupModal={onShowGroupModal}
                onShowManageGroupModal={onShowManageGroupModal}
                onShowManageParticipantsModal={onShowManageParticipantsModal}
                editing={editing}
                currentCSVDownloadButton={
                  editing &&
                  (Object.keys(groups).length || ungroupedEmployees.length) ? (
                    <ParticipantsCurrentCSVDownloadButton
                      onDownloadCsv={onDownloadGroupCsv}
                    />
                  ) : null
                }
                exampleCSVDownloadButton={
                  <InitiativeExampleCSVDownloadButton />
                }
              />
            </Col>
          </Row>

          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Add Stakeholders</h3>
          </div>
          <Row className="m-b-24">
            <Col xs={8}>
              <StakeholdersSelect
                orgStakeholders={orgStakeholders}
                selectedGroupStakeholders={groupStakeholders}
                updateSelectedGroupStakeholders={onSelectGroupStakeholder}
                updateSelectedIndividuals={onSelectIndividualStakeholder}
                selectedIndividuals={individuals}
                uploadMethod={uploadStakeholdersMethod}
                replaceMethod={replaceStakeholdersMethod}
                setUploadMethod={setUploadStakeholdersMethod}
                setReplaceMethod={setStakeholdersReplaceMethod}
                editing={editing}
                currentCSVDownloadButton={
                  editing &&
                  (groupStakeholders.length || individuals.length) ? (
                    <StakeholdersCurrentCSVDownloadButton
                      onDownloadCsv={onDownloadStakeholdersCsv}
                    />
                  ) : null
                }
                exampleCSVDownloadButton={<StakeholdersExampleCSVButton />}
              />
            </Col>
          </Row>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Participants</h3>
          </div>
          <p className="text-muted m-b-48">
            It’s not allowed to change participants in outdated{' '}
            {getOrgTermsContent('initiatives')}. If you want to proceed, please
            correct {getOrgTermsContent('initiative')} dates first.
          </p>
        </React.Fragment>
      )}

      <Row className="m-b-48">
        {submitFailed && (
          <FormGroup validationState="error">
            <HelpBlock>
              {submitError || 'Please fill in all required fields'}
            </HelpBlock>
          </FormGroup>
        )}
        <Col xs={12}>
          <div className="flex flex-justify">
            <div>
              <Button bsStyle="default" disabled={isLoading} onClick={onCancel}>
                Cancel
              </Button>

              <Button
                className="m-l-12"
                bsStyle="primary"
                disabled={isLoading}
                onClick={handleSubmit}
                type="button"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
            <div>{renderPreviewBtn()}</div>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default connect(mapStateToProps)(InitiativeForm);
